import { FOOTER } from "@/constants/testIds";
import Spade from "@/components/Spade";
import { Instagram, MessageCircle, Mail, Copy, LogIn } from "lucide-react";
import { toast } from "sonner";

const WA_URL =
    "https://wa.me/919717622852?text=" +
    encodeURIComponent("Hi, I want to book my $49 Aestherium Media trial week.");

export default function Footer({ site }) {
    const email = site?.email || "aestheriumofficial@gmail.com";
    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(email);
            toast.success("Email copied to clipboard");
        } catch {
            toast.error("Could not copy. Please copy manually");
        }
    };

    return (
        <footer
            data-testid={FOOTER.root}
            className="relative pt-24 pb-10 mt-6 border-t border-[var(--border-subtle)] overflow-hidden"
        >
            <div className="absolute inset-0 -z-10 flex items-center justify-center opacity-[0.05] pointer-events-none">
                <Spade size={720} />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
                    <div>
                        <div className="flex items-center gap-2">
                            <Spade size={28} />
                            <span className="font-display text-2xl text-[var(--text-primary)]">
                                Aestherium<span className="text-[var(--accent-gold)]">.</span>
                                <sup className="text-xs text-[var(--accent-gold)] ml-1">®</sup>
                            </span>
                        </div>
                        <p className="mt-4 text-[var(--text-secondary)] max-w-xs leading-relaxed">
                            Organic Reach. Real Sales. Zero Ads. Est. 2020.
                        </p>
                    </div>

                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent-gold)] mb-4">
                            Reach us
                        </p>
                        <ul className="space-y-3">
                            <li>
                                <a
                                    data-testid={FOOTER.instagram}
                                    href={`https://instagram.com/${site?.instagram || "aestherium.media"}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-3 text-[var(--text-primary)] hover:text-[var(--accent-champagne)] transition-colors group"
                                >
                                    <span className="w-9 h-9 rounded-full border border-[var(--border-subtle)] flex items-center justify-center group-hover:border-[var(--accent-gold)] transition-colors">
                                        <Instagram size={15} />
                                    </span>
                                    @{site?.instagram || "aestherium.media"}
                                </a>
                            </li>
                            <li>
                                <a
                                    data-testid={FOOTER.whatsapp}
                                    href={WA_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-3 text-[var(--text-primary)] hover:text-[var(--accent-champagne)] transition-colors group"
                                >
                                    <span className="w-9 h-9 rounded-full border border-[var(--border-subtle)] flex items-center justify-center group-hover:border-[var(--accent-gold)] transition-colors">
                                        <MessageCircle size={15} />
                                    </span>
                                    {site?.whatsapp || "+91 97176 22852"}
                                </a>
                            </li>
                            <li>
                                <button
                                    data-testid={FOOTER.email}
                                    onClick={copyEmail}
                                    className="inline-flex items-center gap-3 text-[var(--text-primary)] hover:text-[var(--accent-champagne)] transition-colors group"
                                >
                                    <span className="w-9 h-9 rounded-full border border-[var(--border-subtle)] flex items-center justify-center group-hover:border-[var(--accent-gold)] transition-colors">
                                        <Mail size={15} />
                                    </span>
                                    {email}
                                    <Copy
                                        size={12}
                                        className="text-[var(--accent-gold)]/60"
                                    />
                                </button>
                            </li>
                            <li>
                                <a
                                    data-testid={FOOTER.clientLink}
                                    href="/portal"
                                    className="inline-flex items-center gap-3 text-[var(--text-primary)] hover:text-[var(--accent-champagne)] transition-colors group"
                                >
                                    <span className="w-9 h-9 rounded-full border border-[var(--border-subtle)] flex items-center justify-center group-hover:border-[var(--accent-gold)] transition-colors">
                                        <LogIn size={15} />
                                    </span>
                                    Client Login
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent-gold)] mb-4">
                            Registered
                        </p>
                        <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                            <li>
                                <span className="text-[var(--text-primary)]">GST:</span>{" "}
                                {site?.gst || "07FBKPA2936P1Z8"}
                            </li>
                            <li>
                                <span className="text-[var(--text-primary)]">MSME:</span>{" "}
                                {site?.msme || "UDYAM-UK-02-0004950"}
                            </li>
                            <li>
                                <span className="text-[var(--text-primary)]">IEC:</span>{" "}
                                {site?.iec || "FBKPA2936P"}
                            </li>
                            <li className="text-[var(--text-secondary)]/70 mt-3">
                                {site?.address || "Dehradun, Uttarakhand, India"}
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-[var(--border-subtle)] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[var(--text-secondary)]/70">
                    <p>
                        © {new Date().getFullYear()} Aestherium Media ®. All rights reserved.
                        www.aestheriummedia.com
                    </p>
                    <div className="flex items-center gap-4">
                        <span className="font-fraktur text-[var(--accent-gold)]/60">
                            organic. only.
                        </span>
                        <a
                            data-testid={FOOTER.adminLink}
                            href="/admin"
                            className="opacity-40 hover:opacity-100 hover:text-[var(--accent-champagne)] transition-opacity"
                        >
                            admin
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
